
import { useState, useEffect } from 'react';
import { supabase, GalleryItem } from '../../../lib/supabase';
import GalleryItemModal from './GalleryItemModal';

export default function GalleryManager() {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('gallery_items')
      .select('*')
      .order('created_at', { ascending: false });
    setItems(data || []);
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    await supabase.from('gallery_items').delete().eq('id', id);
    setItems(prev => prev.filter(i => i.id !== id));
    setDeletingId(null);
    setConfirmDeleteId(null);
  };

  const handleEdit = (item: GalleryItem) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleNew = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white text-xl font-bold font-['Montserrat']">Galería</h2>
          <p className="text-gray-500 text-sm mt-1">
            {items.length} elemento{items.length !== 1 ? 's' : ''} en la galería
          </p>
        </div>
        <button
          onClick={handleNew}
          className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] text-white font-bold text-sm px-5 py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </div>
          Añadir elemento
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-800 rounded-xl">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-gray-700">
            <i className="ri-image-line text-4xl"></i>
          </div>
          <p className="text-gray-500 text-sm">No hay elementos en la galería.</p>
          <button
            onClick={handleNew}
            className="mt-4 text-cyan-400 hover:text-cyan-300 text-sm underline cursor-pointer"
          >
            Añadir el primero
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map(item => (
            <div
              key={item.id}
              className="group bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl overflow-hidden transition-all"
            >
              {/* Thumbnail */}
              <div className="relative w-full h-36 overflow-hidden bg-gray-800">
                <img
                  src={item.thumbnail_url || item.url}
                  alt={item.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-2 right-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    item.type === 'video'
                      ? 'bg-fuchsia-500/80 text-white'
                      : 'bg-cyan-500/80 text-white'
                  }`}>
                    {item.type === 'video' ? (
                      <span className="flex items-center gap-1">
                        <i className="ri-play-fill text-xs"></i> Vídeo
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <i className="ri-image-line text-xs"></i> Foto
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {/* Info + actions */}
              <div className="p-3">
                <p className="text-white text-xs font-semibold truncate mb-3">{item.title}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="flex-1 flex items-center justify-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white text-xs py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-edit-line text-xs"></i>
                    Editar
                  </button>
                  {confirmDeleteId === item.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(item.id)}
                        disabled={deletingId === item.id}
                        className="bg-red-600 hover:bg-red-500 text-white text-xs px-2 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {deletingId === item.id ? '...' : 'Sí'}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="bg-gray-700 text-gray-300 text-xs px-2 py-1.5 rounded-lg cursor-pointer whitespace-nowrap"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(item.id)}
                      className="w-7 h-7 flex items-center justify-center bg-gray-800 hover:bg-red-900/40 text-gray-400 hover:text-red-400 rounded-lg transition-all cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <GalleryItemModal
          item={editingItem}
          onClose={() => setModalOpen(false)}
          onSaved={fetchItems}
        />
      )}
    </div>
  );
}
